// src/services/auth.service.ts
import { LoginResponse, User, PhaseResponse } from '../types/auth.types';
import { getServerUrl, getCurrentApiUrl } from '../config/server.config';
interface ResetPasswordData {
  email: string;
  code: string;
  newPassword: string;
}

interface VerifyEmailData {
  email: string;
  code: string;
}

interface RegisterData {
  email: string;
  password: string;
  referralCode?: string;
}

class AuthService {
  private tokenKey = 'token';
  private userKey = 'user';
  
  // URL base de la API que cambiará según el entorno
  private baseUrl: string;

  constructor() {
    // Determinar la URL base según el entorno usando getCurrentApiUrl
    this.baseUrl = getCurrentApiUrl();
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Asegurar que el endpoint comienza con '/'
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Construir la URL completa
    const url = `${this.baseUrl}${path}`;
    
    // Log para debuggear
    if (process.env.NODE_ENV === 'development') {
      console.log(`[AuthService] Haciendo petición a: ${url}`);
    }
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Request failed: ${response.statusText}`);
    }

    return response.json();
  }

  async register(data: RegisterData): Promise<void> {
    try {
      // Log para debugging en desarrollo
      if (data.referralCode && process.env.NODE_ENV === 'development') {
        console.log('[AuthService] Registering with referral code:', data.referralCode);
      }
      
      await this.makeRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw new Error('Registration failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async login(email: string, password: string, remember: boolean = false, twoFactorToken?: string): Promise<LoginResponse> {
    try {
      const data = await this.makeRequest<LoginResponse>('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
        },
        body: JSON.stringify({ email, password, twoFactorToken }),
        mode: 'cors', // This is the default and is correct for cross-origin requests
      });
      

      if (data.token) {
        const storage = remember ? localStorage : sessionStorage;
        storage.setItem(this.tokenKey, data.token);
        storage.setItem(this.userKey, JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      throw new Error('Login failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async requestPasswordReset(email: string): Promise<{ message: string; userExists?: boolean }> {
    try {
      const response = await this.makeRequest<{ message: string; userExists?: boolean }>('/auth/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return response;
    } catch (error) {
      console.error('Password reset request error:', error);
      throw new Error('Failed to send reset code: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async resendVerificationCode(email: string): Promise<void> {
    try {
      await this.makeRequest('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    } catch (error) {
      console.error('Resend verification error:', error);
      throw new Error('Failed to resend verification code: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async resetPassword(resetData: ResetPasswordData): Promise<void> {
    try {
      await this.makeRequest('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(resetData),
      });
    } catch (error) {
      console.error('Reset password error:', error);
      throw new Error('Failed to reset password: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async validateEmailToken(data: VerifyEmailData): Promise<void> {
    try {
      await this.makeRequest('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Email verification error:', error);
      throw new Error('Failed to verify email: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  async checkCurrentPhase(token: string): Promise<PhaseResponse> {
    try {
      // Intentar primero con la ruta fast
      let response = await fetch(`${getServerUrl()}/fast/current-phase`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': '*/*',
          'Content-Type': 'application/json'
        }
      });
      // Si no hay respuesta del fast, intentar con la ruta normal
      if (!response.ok) {
        response = await fetch(`${getServerUrl()}/onboarding/current-phase`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': '*/*',
            'Content-Type': 'application/json'
          }
        });
      }

      if (!response.ok) {
        throw new Error('Failed to get current phase');
      }

      const data = await response.json();
      // console.log('Phase check data:', data);
      return data;
    } catch (err) {
      console.error('Error checking phase:', err);
      throw err;
    }
  }

  async checkTicketStatus () {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${getServerUrl()}/support/zenus/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();        
        return data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  getStoredToken(): string | null {
    const token = localStorage.getItem(this.tokenKey) || sessionStorage.getItem(this.tokenKey);
    return token ? token.trim() : null;
  }

  getAuthorizationHeader(): string {
    const token = this.getStoredToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return `Bearer ${token}`;
  }

  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.userKey) || sessionStorage.getItem(this.userKey);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  logout(): void {
    // 1. PRIMERO obtener el token ANTES de eliminarlo
    const token = this.getStoredToken();

    // 2. SEGUNDO hacer la petición al backend si hay token válido
    if (token) {
      fetch(`${getServerUrl()}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => {
        // Silenciar errores de logout - no importa si falla
        console.debug('Logout request failed (this is ok):', error);
      });
    }

    // 3. TERCERO limpiar el storage local
    [localStorage, sessionStorage].forEach(storage => {
      storage.removeItem(this.tokenKey);
      storage.removeItem(this.userKey);
    });

    // 4. FINALMENTE redirigir a la página de login
    const loginUrl = '/';
    if (window.location.pathname !== loginUrl) {
      window.location.href = loginUrl;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getStoredToken();
    if (!token) return false;

    try {
      const payloadBase64 = token.split('.')[1];
      const payload = JSON.parse(atob(payloadBase64));
      const expirationTime = payload.exp * 1000;
      return Date.now() < expirationTime;
    } catch {
      return false;
    }
  }

  // Método para obtener la URL base actual (útil para depuración)
  getBaseUrl(): string {
    return this.baseUrl;
  }
}

export const authService = new AuthService();